/*
 * Copyright (c) 2025. Bolmate B.V.
 */
import './style/reviewNotifications.scss';

import React, {MouseEventHandler, useEffect, useRef, useState} from 'react';
import {NotificationInterval} from "../../../util/enums";
import {Rating} from "../../../util/types";
import Notification from "../../../models/notificationModel";
import {
    AwesomeIcon, ChangeKeyFunction, Checkbox, CloseButton, hasHover, Loader, voidFunction
} from '@dvrd/dvr-controls';
import {BolmateSelectItem} from "../../../util/interfaces";
import ProductSelectController, {ProductSelectHandle} from "../../controls/productSelect/productSelectController";
import {ReviewNotificationSettings} from "../../../util/reviewAnalyticsUtil";
import {BolmateInput, BolmateSelect} from "../../controls";
import {renderPlural} from "../../../util/utils";
import BolRating from "../../bolRating/bolRating";
import classNames from 'classnames';
import IconWithLabel from "../../icon/iconWithLabel";

interface Props {
    onAddNotification: MouseEventHandler;
    onChangeInterval: ChangeKeyFunction<string, NotificationInterval>;
    onChangeEmailAddresses: ChangeKeyFunction<string, string>;
    onChangeProducts: (notificationID: string, products: Array<string>) => void;
    onSelectRating: (notificationID: string, rating: Rating) => MouseEventHandler;
    onDeleteNotification: (notificationID: string) => MouseEventHandler;
    onSendTestNotification: (notification: Notification) => MouseEventHandler;
    notifications: Array<Notification>;
    loading: boolean;
    moduleActive: boolean;
}

const intervalItems: Array<BolmateSelectItem> = [
    {value: '', label: 'Wanneer', selectable: false},
    {value: NotificationInterval.DAY, label: 'dag'},
    {value: NotificationInterval.WEEK, label: 'week'},
    {value: NotificationInterval.MONTH, label: 'maand'},
];

interface NotificationProps extends Props {
    notification: Notification;
}

function ReviewNotification(props: NotificationProps) {
    const {
        notification, onChangeInterval, onChangeProducts, loading, onSelectRating, onChangeEmailAddresses,
        onDeleteNotification, moduleActive, onSendTestNotification
    } = props;
    const [ratingsOpen, setRatingsOpen] = useState(false);
    const productsRef = useRef<ProductSelectHandle>(null);
    const ratingsRef = useRef<HTMLDivElement>(null);

    function onDocumentClick() {
        if (!hasHover(ratingsRef.current)) setRatingsOpen(false);
    }

    function _onChangeProducts(selectedProducts: Array<string>) {
        onChangeProducts(notification.id, selectedProducts);
        productsRef.current?.close();
    }

    function onOpenProducts() {
        productsRef.current?.activate({selectedEANS: new ReviewNotificationSettings(notification.flex).eans});
    }

    function onOpenRatings() {
        setRatingsOpen(!ratingsOpen);
    }

    function onCloseRatings() {
        setRatingsOpen(false);
    }

    function renderInterval() {
        return (
            <section>
                <label className='fixed-text'>gebundeld per</label>
                <BolmateSelect value={notification.interval} onChange={onChangeInterval(notification.id)}
                               items={intervalItems} className='select-field' disabled={loading || !moduleActive}/>
            </section>
        )
    }

    function renderProducts() {
        const selected = new ReviewNotificationSettings(notification.flex).eans;
        let label: string;
        if (selected.has('all') || !selected.size) label = 'Alle producten';
        else label = `${selected.size} ${renderPlural(selected.size, 'product', 'producten')}`;
        return (
            <section>
                <label className='fixed-text'>voor</label>
                {renderButton(label, onOpenProducts, 'products-button')}
            </section>
        )
    }

    function renderRatings() {
        let label: string;
        const selected = new ReviewNotificationSettings(notification.flex).ratings;
        if (selected.size === 5 || !selected.size) label = 'Alle scores';
        else if (selected.size === 1) {
            const rating = Array.from(selected)[0];
            label = `${rating} ${renderPlural(rating, 'ster', 'sterren')}`;
        } else label = `${selected.size} scores`;
        return (
            <section className='ratings-container' ref={ratingsRef}>
                <label className='fixed-text'>Bij nieuwe reviews met</label>
                {renderButton(label, onOpenRatings, 'score-button')}
                <div className={classNames('ratings-select-container', (ratingsOpen && moduleActive) && 'open')}>
                    <CloseButton onClick={onCloseRatings} className='close-ratings'/>
                    {renderRating(1)}
                    {renderRating(2)}
                    {renderRating(3)}
                    {renderRating(4)}
                    {renderRating(5)}
                </div>
            </section>
        )
    }

    function renderRating(rating: Rating) {
        const selected = new ReviewNotificationSettings(notification.flex).ratings.has(rating);
        return (
            <div className='rating-container' onClick={onSelectRating(notification.id, rating)}>
                <Checkbox checked={selected} onCheck={voidFunction}/>
                <BolRating value={rating} staticStars/>
            </div>
        )
    }

    function renderButton(label: string, onClick: MouseEventHandler, className?: string) {
        const disabled = loading || !moduleActive;
        return (
            <div className={classNames('var-button', disabled && 'disabled', className)}
                 onClick={disabled ? undefined : onClick}>
                <label className='button-label'>{label}</label>
            </div>
        )
    }

    function renderEmail() {
        const value = notification.emailAddresses.join(',');
        return (
            <section>
                <label className='fixed-text'>stuur een notificatie naar</label>
                <BolmateInput value={value} inputProps={{type: 'email', multiple: true}}
                              onChange={onChangeEmailAddresses(notification.id)} className='email-field'
                              disabled={loading || !moduleActive}/>
            </section>
        )
    }

    function renderActions() {
        if (!moduleActive) return null;
        return (
            <div className='actions-container'>
                <IconWithLabel label='Notificatie verwijderen' name='trash-alt'
                               onClick={onDeleteNotification(notification.id)} iconClassName='action btn-delete'/>
                <IconWithLabel label='Test notificatie verzenden' name='paper-plane'
                               onClick={onSendTestNotification(notification)} iconClassName='action btn-test'/>
            </div>
        )
    }

    useEffect(() => {
        document.addEventListener('click', onDocumentClick);

        return function() {
            document.removeEventListener('click', onDocumentClick);
        }
    }, []);

    return (
        <>
            <div className='review-notification'>
                <label className='notification-title'>Review notificatie</label>
                <div className='notification'>
                    {renderRatings()}
                    {renderProducts()}
                    {renderEmail()}
                    {renderInterval()}
                </div>
                {moduleActive && renderActions()}
            </div>
            <ProductSelectController onSubmit={_onChangeProducts} ref={productsRef} loading={loading}/>
        </>
    )
}

export default function ReviewNotifications(props: Props) {
    const {onAddNotification, notifications, loading, moduleActive} = props;


    function renderNotification(notification: Notification, index: number) {
        return <ReviewNotification key={index} notification={notification} {...props}/>;
    }

    function renderAddButton() {
        return (
            <div className='btn-add' onClick={onAddNotification}>
                <div className='plus-circle'>
                    <AwesomeIcon name='plus' className='plus-icon'/>
                </div>
                <label className='plus-label'>Voeg een e-mail notificatie toe</label>
            </div>
        );
    }

    return (
        <div className='notification-type review-notifications'>
            <Loader active={loading}/>
            <div className='type-header-container'>
                <label className='notification-type-title'>Notificaties</label>
                <label className='sub-title'>Ontvang een melding per e-mail bij onderstaande triggers:</label>
            </div>
            <div className='notifications'>
                {(moduleActive ? notifications : []).map(renderNotification)}
            </div>
            {renderAddButton()}
        </div>
    )
}